/**
 * TabContext — system kart (zakładek) powłoki, wzorowany 1:1 na SellCall
 * (`apps/admin/src/contexts/TabContext.tsx` w repo referencyjnym).
 *
 * Model: karty napędzane nawigacją. Zmiana location (klik w menu, otwarcie dokumentu)
 * otwiera nową kartę albo uaktywnia istniejącą (dedup po `basePath` = ścieżka bez query).
 * Klik w kartę nawiguje na jej ścieżkę. Stan kart przeżywa F5 (localStorage).
 *
 * Keep-alive (żywe karty w tle) realizuje `TabPanels` — patrz `components/tabs/TabPanels.tsx`.
 *
 * Provider jest SPARAMETRYZOWANY (storageKey / defaultPath / resolveTabMeta), żeby ten sam
 * mechanizm obsłużył panel tenanta i panel admina (różne klucze storage + słowniki etykiet).
 * Karta `defaultPath` (np. Pulpit) jest PRZYPIĘTA — zawsze pierwsza, nieusuwalna.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// ─── Typy ────────────────────────────────────────────────────────────────────
export interface Tab {
  id: string;
  /** Pełna ścieżka z query. */
  path: string;
  /** Ścieżka bez query — klucz deduplikacji. */
  basePath: string;
  label: string;
  /** Nazwa ikony lucide (renderowana przez `renderIconByName`). */
  icon: string;
  openedAt: number;
}

/** Metadane karty (etykieta + ikona) wyliczane ze ścieżki — dostarczane per scope. */
export interface TabMeta {
  label: string;
  icon: string;
  /**
   * Etykieta ze ścieżki to tylko zastępstwo — prawdziwą (nazwę z danych) nadaje strona przez
   * `updateTabLabel`. Przy wczytaniu kart z zapisu zostaje więc zapisana etykieta: panel
   * nieaktywnej karty montuje się dopiero przy pierwszym kliknięciu, a do tego czasu karta
   * pokazywałaby zastępstwo.
   */
  keepStoredLabel?: boolean;
}

interface TabContextType {
  tabs: Tab[];
  activeTabId: string | null;
  /** Ścieżka karty przypiętej (Pulpit) — nieusuwalna, zawsze pierwsza. */
  defaultPath: string;
  openTab: (path: string, label?: string, icon?: string) => void;
  closeTab: (tabId: string) => void;
  switchTab: (tabId: string) => void;
  updateTabLabel: (tabId: string, newLabel: string) => void;
  closeOtherTabs: (tabId: string) => void;
  closeAllTabs: () => void;
}

const TabContext = createContext<TabContextType | null>(null);

interface TabProviderProps {
  children: React.ReactNode;
  /** Klucz localStorage — różny per scope (tenant/admin), żeby karty się nie mieszały. */
  storageKey: string;
  /** Ścieżka karty przypiętej / domyślnej (np. `/dashboard`). */
  defaultPath: string;
  /** Wylicza etykietę + ikonę karty z jej ścieżki (słownik per scope). */
  resolveTabMeta: (path: string) => TabMeta;
}

function deriveBasePath(fullPath: string): string {
  return fullPath.split('?')[0] ?? fullPath;
}

// ─── Persystencja w localStorage ───────────────────────────────────────────────
interface StoredTabs {
  tabs: Tab[];
  activeTabId: string | null;
}

export function TabProvider({
  children,
  storageKey,
  defaultPath,
  resolveTabMeta,
}: TabProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const isPinned = useCallback(
    (tab: Pick<Tab, 'basePath'>) => tab.basePath === defaultPath,
    [defaultPath],
  );

  const makeDefaultTab = useCallback((): Tab => {
    const meta = resolveTabMeta(defaultPath);
    return {
      id: crypto.randomUUID(),
      path: defaultPath,
      basePath: defaultPath,
      label: meta.label,
      icon: meta.icon,
      openedAt: Date.now(),
    };
  }, [defaultPath, resolveTabMeta]);

  const ensureDefaultFirst = useCallback(
    (tabs: Tab[]): Tab[] => {
      const idx = tabs.findIndex((t) => isPinned(t));
      if (idx <= 0) return idx === 0 ? tabs : [makeDefaultTab(), ...tabs];
      const pinned = tabs[idx];
      if (!pinned) return tabs;
      return [pinned, ...tabs.slice(0, idx), ...tabs.slice(idx + 1)];
    },
    [isPinned, makeDefaultTab],
  );

  const loadFromStorage = useCallback((): StoredTabs => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const data = JSON.parse(raw) as StoredTabs;
        if (Array.isArray(data.tabs) && data.tabs.length > 0) {
          // Przelicz etykietę + ikonę ze ścieżki przy wczytaniu — żeby odzwierciedlały
          // bieżący język/słownik i18n (zapisane etykiety mogą być sprzed zmiany języka
          // albo sprzed dodania kluczy). path/basePath/id/openedAt zostają z zapisu. Wyjątek:
          // karty z etykietą z danych (`keepStoredLabel`) zachowują zapisaną nazwę.
          const refreshed = data.tabs.map((tab) => {
            const meta = resolveTabMeta(tab.path ?? defaultPath);
            const label = meta.keepStoredLabel && tab.label ? tab.label : meta.label;
            return { ...tab, label, icon: meta.icon };
          });
          return { ...data, tabs: ensureDefaultFirst(refreshed) };
        }
      }
    } catch {
      /* ignore */
    }
    return { tabs: [], activeTabId: null };
  }, [storageKey, ensureDefaultFirst, resolveTabMeta, defaultPath]);

  // Refs sterujące pętlą efektów / synchronizacją URL.
  const isTabSwitchRef = useRef(false);
  const initializedRef = useRef(false);
  const activeBasePathRef = useRef<string>('');

  const [tabs, setTabs] = useState<Tab[]>(() => loadFromStorage().tabs);
  const [activeTabId, setActiveTabId] = useState<string | null>(() => {
    const stored = loadFromStorage();
    if (stored.activeTabId && stored.tabs.some((t) => t.id === stored.activeTabId)) {
      return stored.activeTabId;
    }
    return stored.tabs[0]?.id ?? null;
  });
  // Ostatnie `activeTabId`, dla którego efekt synchronizacji URL już zadziałał. Efekt nawiguje
  // TYLKO przy realnej ZMIANIE aktywnej karty (przełączenie), nie na (pod StrictMode DWUKROTNYM)
  // montażu. Zastępuje dawną jednorazową flagę `skipNextSyncRef`, którą StrictMode konsumował na
  // pierwszym z dwóch przebiegów, przez co drugi (stan sprzed aktywacji deep-linka) nawigował z
  // powrotem na starą kartę — cichy powrót przy deep-linku/F5.
  const lastSyncedActiveIdRef = useRef(activeTabId);

  // ── Metody ──
  const openTab = useCallback(
    (path: string, label?: string, icon?: string) => {
      const basePath = deriveBasePath(path);
      // `id`/`openedAt` liczone RAZ, PRZED updaterem — updater `setTabs` bywa odpalany dwa razy
      // (React 18 double-invoke updatera w dev/StrictMode). Wcześniej `crypto.randomUUID()` żyło
      // WEWNĄTRZ updatera, więc każdy przebieg dawał INNE id (a `setActiveTabId` w updaterze —
      // aktywację na inne id niż karta faktycznie utrwalona). Przy deep-linku/F5 (cold mount) karta
      // docelowa powstawała, ale aktywną zostawała poprzednia (Pulpit), więc URL cicho wracał.
      // Stabilne `id` czyni oba przebiegi updatera idempotentnymi.
      const newId = crypto.randomUUID();
      const openedAt = Date.now();
      setTabs((prev) => {
        const existing = prev.find((t) => t.basePath === basePath);
        if (existing) {
          setActiveTabId(existing.id);
          if (existing.path !== path) {
            return prev.map((t) => (t.id === existing.id ? { ...t, path } : t));
          }
          return prev;
        }
        const meta = resolveTabMeta(path);
        const newTab: Tab = {
          id: newId,
          path,
          basePath,
          label: label || meta.label,
          icon: icon || meta.icon,
          openedAt,
        };
        setActiveTabId(newId);
        return [...prev, newTab];
      });
    },
    [resolveTabMeta],
  );

  const closeTab = useCallback(
    (tabId: string) => {
      setTabs((prev) => {
        const tabIndex = prev.findIndex((t) => t.id === tabId);
        const target = tabIndex === -1 ? undefined : prev[tabIndex];
        if (!target) return prev;
        // Karta przypięta (Pulpit) nieusuwalna.
        if (isPinned(target)) return prev;

        const newTabs = prev.filter((t) => t.id !== tabId);
        if (newTabs.length === 0) {
          const def = makeDefaultTab();
          setActiveTabId(def.id);
          return [def];
        }
        setActiveTabId((prevActive) => {
          if (prevActive === tabId) {
            const nextIndex = Math.min(tabIndex, newTabs.length - 1);
            return newTabs[nextIndex]?.id ?? prevActive;
          }
          return prevActive;
        });
        return newTabs;
      });
    },
    [isPinned, makeDefaultTab],
  );

  const switchTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const updateTabLabel = useCallback((tabId: string, newLabel: string) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, label: newLabel } : t)));
  }, []);

  const closeOtherTabs = useCallback(
    (tabId: string) => {
      setTabs((prev) => ensureDefaultFirst(prev.filter((t) => t.id === tabId || isPinned(t))));
      setActiveTabId(tabId);
    },
    [ensureDefaultFirst, isPinned],
  );

  const closeAllTabs = useCallback(() => {
    const def = makeDefaultTab();
    setTabs([def]);
    setActiveTabId(def.id);
  }, [makeDefaultTab]);

  // ── Karta startowa, gdy brak zapisanych ──
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (tabs.length === 0) {
      const currentPath = location.pathname + location.search;
      const path = currentPath === '/' ? defaultPath : currentPath;
      const meta = resolveTabMeta(path);
      const id = crypto.randomUUID();
      const bp = deriveBasePath(path);
      const newTab: Tab = {
        id,
        path,
        basePath: bp,
        label: meta.label,
        icon: meta.icon,
        openedAt: Date.now(),
      };
      const withDefault = bp === defaultPath ? [newTab] : ensureDefaultFirst([newTab]);
      setTabs(withDefault);
      setActiveTabId(id);
      activeBasePathRef.current = bp;
    } else {
      const active = tabs.find((t) => t.id === activeTabId);
      const currentPath = location.pathname + location.search;
      // Twarda nawigacja (deep-link/bookmark) na ścieżkę inną niż domyślna (Pulpit) i inną niż
      // zapersystowana aktywna karta — otwórz/aktywuj tę kartę zamiast wracać do ostatniej
      // sesji. Wyjątek dla `defaultPath` (nie tylko '/'): logowanie zawsze ląduje na
      // `/dashboard`, więc bez niego ginie „przywróć ostatnią kartę po zalogowaniu".
      if (currentPath !== '/' && currentPath !== defaultPath && currentPath !== active?.path) {
        openTab(currentPath);
        activeBasePathRef.current = deriveBasePath(currentPath);
      } else if (active) {
        activeBasePathRef.current = active.basePath;
        if (active.path !== currentPath) {
          isTabSwitchRef.current = true;
          navigate(active.path, { replace: true });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Synchronizacja URL przy zmianie aktywnej karty ──
  useEffect(() => {
    if (!initializedRef.current) return;
    // Nawigujemy TYLKO przy realnej zmianie aktywnej karty (przełączenie zakładki). Na (pod
    // StrictMode DWUKROTNYM) montażu `activeTabId` równa się wartości ostatnio zsynchronizowanej,
    // więc pomijamy — inaczej stan sprzed aktywacji deep-linka nawigowałby z powrotem na starą kartę.
    if (activeTabId === lastSyncedActiveIdRef.current) return;
    lastSyncedActiveIdRef.current = activeTabId;
    const active = tabs.find((t) => t.id === activeTabId);
    if (!active) return;
    const currentPath = location.pathname + location.search;
    if (active.path !== currentPath) {
      isTabSwitchRef.current = true;
      navigate(active.path, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // ── Trzymaj activeBasePathRef w zgodzie ──
  useEffect(() => {
    const active = tabs.find((t) => t.id === activeTabId);
    if (active) activeBasePathRef.current = active.basePath;
  }, [tabs, activeTabId]);

  // ── Wykryj nawigację w aplikacji: otwórz nową kartę lub zaktualizuj bieżącą ──
  useEffect(() => {
    if (isTabSwitchRef.current) {
      isTabSwitchRef.current = false;
      return;
    }
    if (!activeTabId) return;
    const currentPath = location.pathname + location.search;
    const currentBasePath = deriveBasePath(currentPath);

    if (activeBasePathRef.current && activeBasePathRef.current !== currentBasePath) {
      // Znacznik „zastąp bieżącą kartę" (zapis nowego rekordu: /X/new → /X/:id) — przekształć
      // AKTYWNĄ kartę w miejscu (ten sam id, nowa ścieżka/basePath/etykieta) zamiast otwierać
      // drugą, żeby karta „nowy" stała się kartą zapisanego rekordu, a nie została sierotą obok.
      // Idempotentne: ponowny przebieg trafia w gałąź „else" (basePath już zgodny). Znacznik jest
      // ULOTNY — niesie go pojedyncza nawigacja po zapisie (patrz przepływy create w kartotekach);
      // kolejne wewnętrzne nawigacje bez niego nie przekształcają.
      const replaceTab = (location.state as { replaceTab?: boolean } | null)?.replaceTab === true;
      // Ochrona dedupu basePath: gdy INNA karta ma już docelowy basePath (np. przeglądarkowe
      // wstecz→dalej zreplayuje ten sam wpis historii ze znacznikiem replaceTab), morfowanie
      // zdublowałoby basePath i złamało niezmiennik „jeden basePath = jedna karta". W takim
      // wypadku aktywujemy istniejącą kartę (openTab robi to dedup-safe), zamiast morfować.
      // `tabs`/`activeTabId` czytamy z domknięcia — celowo POZA deps efektu (deps = tylko `location`):
      // render synchronizuje je z `location` w tym samym commicie, a danie `tabs` do deps zapętliłoby
      // efekt przez `setTabs`.
      const wouldDuplicateBasePath = tabs.some(
        (t) => t.id !== activeTabId && t.basePath === currentBasePath,
      );
      if (replaceTab && !wouldDuplicateBasePath) {
        const meta = resolveTabMeta(currentPath);
        setTabs((prev) =>
          prev.map((t) =>
            t.id === activeTabId
              ? {
                  ...t,
                  path: currentPath,
                  basePath: currentBasePath,
                  label: meta.label,
                  icon: meta.icon,
                }
              : t,
          ),
        );
      } else {
        openTab(currentPath);
      }
    } else {
      setTabs((prev) => prev.map((t) => (t.id === activeTabId ? { ...t, path: currentPath } : t)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // ── Persystencja (debounced) ──
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({ tabs, activeTabId }));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tabs, activeTabId, storageKey]);

  // ── Skróty klawiaturowe: Ctrl/Cmd+W zamyka, Ctrl(+Shift)+Tab przełącza ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
        e.preventDefault();
        if (activeTabId) closeTab(activeTabId);
      }
      if (e.ctrlKey && e.key === 'Tab') {
        e.preventDefault();
        if (tabs.length <= 1) return;
        const idx = tabs.findIndex((t) => t.id === activeTabId);
        const nextIdx = e.shiftKey
          ? (idx - 1 + tabs.length) % tabs.length
          : (idx + 1) % tabs.length;
        const next = tabs[nextIdx];
        if (next) switchTab(next.id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tabs, activeTabId, closeTab, switchTab]);

  return (
    <TabContext.Provider
      value={{
        tabs,
        activeTabId,
        defaultPath,
        openTab,
        closeTab,
        switchTab,
        updateTabLabel,
        closeOtherTabs,
        closeAllTabs,
      }}
    >
      {children}
    </TabContext.Provider>
  );
}

export function useTabs() {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error('useTabs must be used within TabProvider');
  return ctx;
}

/**
 * Wariant „miękki" — zwraca null poza `TabProvider` zamiast rzucać. Dla komponentów,
 * które renderują się też poza powłoką kart (np. strony w testach RTL bez providera)
 * i chcą jedynie best-effort zaktualizować etykietę własnej karty.
 */
export function useTabsOptional() {
  return useContext(TabContext);
}
